// src/shared/composables/useAsyncHandler.ts

import type { ApplicationError } from '@/schemas/errors';
import { classifyError, createError, errorGuards, wrapError } from '@/schemas/errors';
import { captureException, isDiagnosticsEnabled } from '@/services/diagnostics.service';
import { loggingService } from '@/services/logging.service';
import type {} from '@/shared/stores/notificationsStore';
import { readCoordinatorDisposition } from '@/shared/stores/authStore';
import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
import type { NotificationSeverity } from '@/types/ui/notifications';
import { useI18n } from 'vue-i18n';

export interface AsyncHandlerOptions {
  /**
   * Optional handler for user-facing notifications
   */
  notify?: ((message: string, severity: NotificationSeverity) => void) | false;
  /**
   * Optional error logging implementation
   */
  log?: ((error: ApplicationError) => void) | false;
  /**
   * Optional loading state handler
   */
  setLoading?: (isLoading: boolean) => void;
  /**
   * Optional callback for when an error occurs, called before error is thrown
   * Useful for state cleanup, invalidation, etc.
   */
  onError?: (error: ApplicationError) => void;
  debug?: boolean;
}

export { createError, errorGuards, wrapError }; // Re-export for convenience

/**
 * Composable for handling async operations with consistent error handling.
 * Primarily used for API calls and other async operations that need:
 * - Loading state management
 * - Structured error classification
 * - User notifications for human-facing errors
 *
 * NOTE: For component-level error boundaries, use Vue's built-in
 * errorCaptured hook instead.
 *
 * We use the errorHandler composable across our pinia stores. We also have a global
 * GlobalErrorBoundary that logs and notifies users of errors. The useDomainsManager
 * composable uses the errorHandler to for async errors. Other exceptions propogate
 * up to Vue 3 handle errors.
 *
 * Raison d'être:
 *
 * This composable exists to solve three specific problems in async operations:
 *
 * 1. Loading State Management
 *    - Automatically handles loading states for async operations
 *    - Ensures loading state is always cleared, even when errors occur
 *
 * 2. Structured Error Classification
 *    - Transforms various error types into a consistent ApplicationError format
 *    - Distinguishes between technical and human-facing errors
 *    - Enables consistent error handling patterns across the application
 *
 * 3. User Feedback
 *    - Automatically shows notifications for human-facing errors
 *    - Logs technical errors without user notification
 *
 * When NOT to use this composable:
 * - For component error boundaries (use Vue's errorCaptured hook)
 * - For global error handling (use Vue's app.config.errorHandler)
 * - For synchronous operations
 *
 * Example usage:
 *
 *    ```ts
 *    const { wrap } = useAsyncHandler({
 *      notify: useNotifications(),
 *      setLoading: useLoadingState(),
 *      log: useLogger()
 *    });
 *
 *    // In an async operation:
 *    const data = await wrap(async () => {
 *      const response = await api.fetchData();
 *      return response.data;
 *    });
 * ```
 */
export function useAsyncHandler(options: AsyncHandlerOptions = {}) {

  // useAsyncHandler is called during component setup (synchronously), so we put
  // useI18n() here to execute in the correct context. The t function it returns
  // is then available via closure when wrap() is called later.
  //
  // Vue composables that use injection (inject, useI18n, useRouter, etc.) must
  // be called synchronously at the top level of setup() or another composable
  // and not never inside callbacks, async functions, or event handlers.
  const { t } = useI18n();

  // Capture bootstrap store for Sentry context tags (jurisdiction, planid, role)
  const bootstrap = useBootstrapStore();

  // Default implementations that will be used if no options provided
  const handlers = {
    notify:
      options.notify === false || options.notify === null
        ? undefined
        : (options.notify ??
          ((message: string, severity: NotificationSeverity) => {
            loggingService.info(`[notify] [${severity}] ${message}`);
          })),
    // Only set default logger if log isn't explicitly false/null
    log:
      options.log === false || options.log === null
        ? undefined
        : (options.log ??
          ((error: ApplicationError) => {
            loggingService.error(error);
          })),
    setLoading: options.setLoading ?? (() => {}),
    onError: options.onError,
  };

  /**
   * Safely invokes the onError callback, logging any callback errors
   */
  function handleErrorCallback(classifiedError: ApplicationError): void {
    if (!handlers.onError) return;
    try {
      handlers.onError(classifiedError);
    } catch (callbackError) {
      handlers.log?.(classifyError(callbackError as Error));
    }
  }

  /**
   * Notifies the user with appropriate message based on error type
   */
  function notifyUser(classifiedError: ApplicationError): void {
    if (!handlers.notify) return;
    const isHuman = errorGuards.isOfHumanInterest(classifiedError);
    const message = isHuman ? classifiedError.message : t('web.COMMON.unexpected_error');
    handlers.notify(message, classifiedError.severity);
  }

  /**
   * Whether the refresh coordinator owns the user-visible message for this
   * error (#4461, ADR-046#rejection-disposition).
   *
   * The list of carve-outs (`admin_session`, `awaiting_mfa` on an MFA-pending
   * tab, an anonymous tab, throttled duplicates) lives in exactly one place:
   * `authStore.noteApiRejection`. The interceptor stamps that decision on
   * the error via `COORDINATOR_DISPOSITION_KEY`, and this reads that one
   * field. If it is absent (a non-401, an error that never touched the
   * interceptor, or a bootstrap-time rejection where Pinia wasn't ready) we
   * treat it as not owned — the caller's toast stands.
   */
  function coordinatorOwnsMessage(error: unknown): boolean {
    return readCoordinatorDisposition(error)?.ownedByCoordinator === true;
  }

  /**
   * Logs technical errors and sends to Sentry with context tags
   */
  function logTechnicalError(error: unknown, classifiedError: ApplicationError): void {
    if (errorGuards.isOfHumanInterest(classifiedError)) return;

    handlers.log?.(classifiedError);

    if (isDiagnosticsEnabled()) {
      captureException(error instanceof Error ? error : new Error(String(error)), {
        errorType: classifiedError.type,
        service: 'web',
        jurisdiction: bootstrap.regions?.current_jurisdiction,
        planid: bootstrap.organization?.planid,
        role: bootstrap.cust?.role,
      });
    }
  }

  /**
   * Wraps an async operation with consistent error handling
   *
   * Key features:
   * - Loading state management
   * - Error classification and structured handling
   * - User notifications for human-facing errors
   * - Error logging and optional error callbacks
   * - Error boundary - stops error propagation
   *
   * @example
   * ```ts
   * const { wrap } = useAsyncHandler({
   *   notify: (msg, severity) => toast(msg, severity),
   *   setLoading: (isLoading) => store.setLoading(isLoading),
   *   onError: (error) => store.reset()
   * });
   *
   * // In a component or service:
   * const data = await wrap(() => api.fetchData());
   * ```
   */
  async function wrap<T>(operation: () => Promise<T>): Promise<T | undefined> {
    try {
      handlers.setLoading?.(true);
      return await operation();
    } catch (error) {
      const classifiedError = classifyError(error as Error);

      handleErrorCallback(classifiedError);
      if (!coordinatorOwnsMessage(error)) notifyUser(classifiedError);
      logTechnicalError(error, classifiedError);

      return undefined;
    } finally {
      handlers.setLoading?.(false);
    }
  }

  return { wrap, wrapError, createError };
}
